import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

/**
 * Voice-to-text dictation, hold-to-talk.
 *
 * Native (Capacitor): @capacitor-community/speech-recognition. Uses Android's
 *   SpeechRecognizer service and iOS's SFSpeechRecognizer.
 * Web: Web Speech API (window.SpeechRecognition / webkitSpeechRecognition).
 *
 * The hook prefers the native plugin when running inside Capacitor, but falls
 * back to Web Speech API if the plugin isn't bundled in the host APK/IPA —
 * Capacitor's WebView (Chromium on Android) ships webkitSpeechRecognition, so
 * users on older app versions still get the mic button while their installed
 * binary catches up to a release that includes the plugin.
 *
 * Caller drives start() on press and stop() on release.
 */

// BCP-47 language codes matching our supported languages
const LANG_MAP: Record<string, string> = {
  de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES',
  it: 'it-IT', pt: 'pt-BR', nl: 'nl-NL', sv: 'sv-SE',
  pl: 'pl-PL', cs: 'cs-CZ', tr: 'tr-TR', uk: 'uk-UA',
  ja: 'ja-JP', ko: 'ko-KR',
};

const IS_NATIVE = Capacitor.isNativePlatform();

type Mode = 'native' | 'web' | null;

interface SpeechRecognitionHook {
  isListening: boolean;
  isSupported: boolean;
  start: () => void;
  stop: () => void;
}

const hasWebSpeech = () =>
  !!((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition);

export default function useSpeechRecognition(
  onResult: (transcript: string) => void,
  language: string = 'de',
): SpeechRecognitionHook {
  const [isListening, setIsListening] = useState(false);
  const [mode, setMode] = useState<Mode>(null);
  // True while the user is physically holding the mic button.
  const isHeldRef = useRef(false);
  // Latest callbacks captured per-render so async closures call the up-to-date handler.
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const langRef = useRef(language);
  langRef.current = language;

  // Web Speech API state
  const recognitionRef = useRef<any>(null);
  // Native plugin state
  const partialListenerRef = useRef<PluginListenerHandle | null>(null);
  const stateListenerRef = useRef<PluginListenerHandle | null>(null);
  const latestPartialRef = useRef('');

  // Pick the active backend on mount. Prefers the native plugin when both the
  // Capacitor runtime AND the plugin are present; falls back to Web Speech
  // when the plugin isn't registered (e.g. installed APK predates the release
  // that bundled it) or when running in a regular browser.
  useEffect(() => {
    let cancelled = false;
    const detect = async () => {
      if (IS_NATIVE && Capacitor.isPluginAvailable('SpeechRecognition')) {
        try {
          const r = await SpeechRecognition.available();
          if (cancelled) return;
          if (r.available) { setMode('native'); return; }
        } catch { /* fall through to web */ }
      }
      if (!cancelled && hasWebSpeech()) setMode('web');
    };
    void detect();
    return () => { cancelled = true; };
  }, []);

  // ─── Native (Capacitor plugin) ──────────────────────────────────────

  const cleanupNativeListeners = async () => {
    try { await partialListenerRef.current?.remove(); } catch { /* noop */ }
    try { await stateListenerRef.current?.remove(); } catch { /* noop */ }
    partialListenerRef.current = null;
    stateListenerRef.current = null;
  };

  const startNative = async () => {
    if (isHeldRef.current) return;
    const lang = LANG_MAP[langRef.current] || 'de-DE';

    try {
      let perms = await SpeechRecognition.checkPermissions();
      if (perms.speechRecognition !== 'granted') {
        perms = await SpeechRecognition.requestPermissions();
        if (perms.speechRecognition !== 'granted') return;
      }
    } catch {
      return; // permission check itself failed
    }

    latestPartialRef.current = '';

    partialListenerRef.current = await SpeechRecognition.addListener('partialResults', (data) => {
      if (data.matches?.[0]) latestPartialRef.current = data.matches[0];
    });

    stateListenerRef.current = await SpeechRecognition.addListener('listeningState', async (s) => {
      if (s.status !== 'stopped') return;
      // Commit whatever this session captured.
      const final = latestPartialRef.current.trim();
      if (final) onResultRef.current(final);
      latestPartialRef.current = '';
      // While still held, restart so silence-auto-stop doesn't end the hold.
      if (isHeldRef.current) {
        try {
          await SpeechRecognition.start({ language: lang, partialResults: true, popup: false });
          return;
        } catch {
          isHeldRef.current = false;
        }
      }
      setIsListening(false);
      await cleanupNativeListeners();
    });

    try {
      await SpeechRecognition.start({ language: lang, partialResults: true, popup: false });
      isHeldRef.current = true;
      setIsListening(true);
    } catch {
      isHeldRef.current = false;
      setIsListening(false);
      await cleanupNativeListeners();
    }
  };

  const stopNative = async () => {
    isHeldRef.current = false;
    try { await SpeechRecognition.stop(); } catch { /* already stopped */ }
    // The 'listeningState' stopped event will fire onResult and tear down listeners.
  };

  // ─── Web Speech API ────────────────────────────────────────────────

  const buildWebRecognition = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return null;
    const recognition = new SR();
    recognition.lang = LANG_MAP[langRef.current] || 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onresult = (event: any) => {
      const last = event.results[event.results.length - 1];
      if (last.isFinal) onResultRef.current(last[0].transcript);
    };

    recognition.onerror = () => {
      isHeldRef.current = false;
      setIsListening(false);
    };

    recognition.onend = () => {
      if (isHeldRef.current) {
        try { recognition.start(); return; } catch { /* fall through */ }
      }
      setIsListening(false);
    };

    return recognition;
  };

  const startWeb = () => {
    if (isHeldRef.current) return;
    const recognition = buildWebRecognition();
    if (!recognition) return;
    recognitionRef.current = recognition;
    try {
      recognition.start();
      isHeldRef.current = true;
      setIsListening(true);
    } catch {
      isHeldRef.current = false;
      setIsListening(false);
    }
  };

  const stopWeb = () => {
    isHeldRef.current = false;
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
  };

  // ─── Public API ────────────────────────────────────────────────────

  const start = () => {
    if (mode === 'native') void startNative();
    else if (mode === 'web') startWeb();
  };
  const stop = () => {
    if (mode === 'native') void stopNative();
    else if (mode === 'web') stopWeb();
  };

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      isHeldRef.current = false;
      if (mode === 'native') {
        SpeechRecognition.stop().catch(() => { /* noop */ });
        void cleanupNativeListeners();
      } else if (mode === 'web') {
        try { recognitionRef.current?.stop(); } catch { /* noop */ }
      }
    };
  }, [mode]);

  return { isListening, isSupported: mode !== null, start, stop };
}
